import { rem } from 'nativewind'
import { useEffect, useRef } from 'react'
import { Platform, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native'
import type Animated from 'react-native-reanimated'
import {
  Extrapolation,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { useDimensions } from '@/src/general/hooks/useDimensions'
const remValue = rem.get()
export const HEADER_TRANSLATE_Y = 280
const isSteppedHeaderTransitionEnabled = Platform.OS === 'web'

export const useHeaderTranslateY = () => {
  const { top } = useSafeAreaInsets()
  return HEADER_TRANSLATE_Y - top
}

export const useListAnimatedScroll = () => {
  const offsetY = useSharedValue(0)
  const fixedOffsetY = useSharedValue(0)
  const listRef = useRef<Animated.FlatList | null>(null)
  const hasHandledScrollEnd = useRef(false)
  const { width } = useDimensions()
  const translateY = useHeaderTranslateY()

  useEffect(() => {
    listRef.current?.scrollToOffset({ offset: 0, animated: false })
    fixedOffsetY.set(0)
  }, [width, fixedOffsetY])

  const handleSnap = () => {
    requestAnimationFrame(() => {
      if (Math.abs(offsetY.get()) > 0 && Math.abs(offsetY.get()) < 140) {
        listRef.current?.scrollToOffset({ offset: 0, animated: true })
      }
      if (Math.abs(offsetY.get()) >= 140 && Math.abs(offsetY.get()) < translateY) {
        listRef.current?.scrollToOffset({
          offset: translateY,
          animated: true,
        })
      }
    })
  }

  const handleScrollBegin = () => {
    hasHandledScrollEnd.current = false
  }

  const handleScrollEnd = () => {
    if (hasHandledScrollEnd.current) return

    hasHandledScrollEnd.current = true
    handleSnap()
  }

  const handleScrollEndDrag = ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
    // If momentum continues, snap when it ends instead.
    if (nativeEvent.velocity?.y ?? 0) return
    handleScrollEnd()
  }

  const headerAnimatedStyle = useAnimatedStyle(() => ({
    borderBottomLeftRadius: interpolate(offsetY.get(), [-translateY, 0], [0, remValue * 1.5], Extrapolation.CLAMP),
    borderBottomRightRadius: interpolate(offsetY.get(), [-translateY, 0], [0, remValue * 1.5], Extrapolation.CLAMP),
    transform: [{ translateY: offsetY.get() }],
  }))

  const scrollHandler = useAnimatedScrollHandler((event) => {
    if (isSteppedHeaderTransitionEnabled) {
      offsetY.set(event.contentOffset.y >= HEADER_TRANSLATE_Y ? -translateY : 0)
      return
    }

    const t = interpolate(event.contentOffset.y, [0, 140], [0, translateY], Extrapolation.CLAMP)
    offsetY.set(t * -1)
  })

  return {
    scrollHandler,
    offsetY,
    listRef,
    handleScrollBegin,
    handleScrollEnd,
    handleScrollEndDrag,
    fixedOffsetY,
    headerAnimatedStyle,
  }
}
